// cspell:disable

import {
  Crockford32Options,
  crockford32StringFormat,
} from "@redotech/util/crockford32";
import * as z from "zod";

export type AccountId = ID<"account">;
export const accountIdFormat = idFormat<AccountId>({ prefix: "acc_" });

export type NotificationId = ID<"notification">;
export const notificationIdFormat = idFormat<NotificationId>({
  prefix: "ntf_",
});

export type CustomAnalyticsReportId = ID<"customAnalyticsReport">;
export const customAnalyticsReportIdFormat = idFormat<CustomAnalyticsReportId>({
  prefix: "crpt_",
});

export type ProductId = ID<"product">;
export const productIdFormat = idFormat<ProductId>({ prefix: "prd_" });

export type ProductImageId = ID<"productImage">;
export const productImageIdFormat = idFormat<ProductImageId>({
  prefix: "pri_",
});

export type ProductFamilyId = ID<"productFamily">;
export const productFamilyIdFormat = idFormat<ProductFamilyId>({
  prefix: "pfm_",
});

export type ProductFamilyImageId = ID<"productFamilyImage">;
export const productFamilyImageIdFormat = idFormat<ProductFamilyImageId>({
  prefix: "pfi_",
});

export type ProductFamilyOptionId = ID<"productFamilyOption">;
export const productFamilyOptionIdFormat = idFormat<ProductFamilyOptionId>({
  prefix: "pfo_",
});

export type ProductFamilyOptionValueId = ID<"productFamilyOptionValue">;
export const productFamilyOptionValueIdFormat =
  idFormat<ProductFamilyOptionValueId>({ prefix: "pfov_" });

export type WebhookEndpointId = ID<"webhookEndpoint">;
export const webhookEndpointIdFormat = idFormat<WebhookEndpointId>({
  prefix: "whe_",
});

export type WebhookEventId = ID<"webhookEvent">;
export const webhookEventIdFormat = idFormat<WebhookEventId>({
  prefix: "wev_",
});

export type WebhookDeliveryId = ID<"webhookDelivery">;
export const webhookDeliveryIdFormat = idFormat<WebhookDeliveryId>({
  prefix: "whd_",
});

export type LocationId = ID<"location">;
export const locationIdFormat = idFormat<LocationId>({ prefix: "loc_" });

export type StorageLocationId = ID<"storageLocation">;
export const storageLocationIdFormat = idFormat<StorageLocationId>({
  prefix: "sl_",
});

export type StorageLocationGroupId = ID<"storageLocationGroup">;
export const storageLocationGroupIdFormat = idFormat<StorageLocationGroupId>({
  prefix: "slg_",
});

export type StorageLocationInventoryId = ID<"storageLocationInventory">;
export const storageLocationInventoryIdFormat =
  idFormat<StorageLocationInventoryId>({ prefix: "sli_" });

export type StorageLocationTransferId = ID<"storageLocationTransfer">;
export const storageLocationTransferIdFormat =
  idFormat<StorageLocationTransferId>({ prefix: "slt_" });

export type LocationInventoryId = ID<"locationInventory">;
export const locationInventoryIdFormat = idFormat<LocationInventoryId>({
  prefix: "li_",
});

export type ReservedInventoryBucketId = ID<"reservedInventoryBucket">;
export const reservedInventoryBucketIdFormat =
  idFormat<ReservedInventoryBucketId>({ prefix: "rib_" });

export type CustomReservedInventoryBucketTypeId =
  ID<"customReservedInventoryBucketType">;
export const customReservedInventoryBucketTypeIdFormat =
  idFormat<CustomReservedInventoryBucketTypeId>({ prefix: "cribt_" });

export type InventoryAdjustmentReasonId = ID<"inventoryAdjustmentReason">;
export const inventoryAdjustmentReasonIdFormat =
  idFormat<InventoryAdjustmentReasonId>({ prefix: "iar_" });

export type InventoryItemId = ID<"inventoryItem">;
export const inventoryItemIdFormat = idFormat<InventoryItemId>({
  prefix: "ii_",
});

export type InventoryLevelId = ID<"inventoryLevel">;
export const inventoryLevelIdFormat = idFormat<InventoryLevelId>({
  prefix: "il_",
});

export type InventoryLevelLedgerId = ID<"inventoryLevelLedger">;
export const inventoryLevelLedgerIdFormat = idFormat<InventoryLevelLedgerId>({
  prefix: "ill_",
});

export type PricingRuleId = ID<"pricingRule">;
export const pricingRuleIdFormat = idFormat<PricingRuleId>({ prefix: "prl_" });

export type AdjustmentSessionId = ID<"inventoryAdjustmentSession">;
export const adjustmentSessionIdFormat = idFormat<AdjustmentSessionId>({
  prefix: "adj_",
});

export type PackingTemplateId = ID<"packingTemplate">;
export const packingTemplateIdFormat = idFormat<PackingTemplateId>({
  prefix: "pkt_",
});

export type TransferSessionId = ID<"transferSession">;
export const transferSessionIdFormat = idFormat<TransferSessionId>({
  prefix: "trs_",
});

export type BinCountSessionId = ID<"binCountSession">;
export const binCountSessionIdFormat = idFormat<BinCountSessionId>({
  prefix: "bcs_",
});

export type BinAssignmentSessionId = ID<"binAssignmentSession">;
export const binAssignmentSessionIdFormat = idFormat<BinAssignmentSessionId>({
  prefix: "bas_",
});

export type TaskId = ID<"task">;
export const taskIdFormat = idFormat<TaskId>({ prefix: "tsk_" });

export type ProductConversionSessionId = ID<"productConversionSession">;
export const productConversionSessionIdFormat =
  idFormat<ProductConversionSessionId>({ prefix: "pcs_" });

export type ProductConversionSessionLineId = ID<"productConversionSessionLine">;
export const productConversionSessionLineIdFormat =
  idFormat<ProductConversionSessionLineId>({ prefix: "pcl_" });

export type CycleCountRuleId = ID<"cycleCountRule">;
export const cycleCountRuleIdFormat = idFormat<CycleCountRuleId>({
  prefix: "ccr_",
});

export type CycleCountId = ID<"cycleCount">;
export const cycleCountIdFormat = idFormat<CycleCountId>({ prefix: "cyc_" });

export type CycleCountLineId = ID<"cycleCountLine">;
export const cycleCountLineIdFormat = idFormat<CycleCountLineId>({
  prefix: "ccl_",
});

export type TaskReferenceId = ID<"taskReference">;
export const taskReferenceIdFormat = idFormat<TaskReferenceId>({
  prefix: "tkr_",
});

export type TaskAssignmentId = ID<"taskAssignment">;
export const taskAssignmentIdFormat = idFormat<TaskAssignmentId>({
  prefix: "tas_",
});

export type TaskUserEventId = ID<"taskUserEvent">;
export const taskUserEventIdFormat = idFormat<TaskUserEventId>({
  prefix: "tue_",
});

export type TaskUserStateId = ID<"taskUserState">;
export const taskUserStateIdFormat = idFormat<TaskUserStateId>({
  prefix: "tus_",
});

export type ForecastId = ID<"forecast">;
export const forecastIdFormat = idFormat<ForecastId>({ prefix: "fcst_" });

export type SupplierId = ID<"supplier">;
export const supplierIdFormat = idFormat<SupplierId>({ prefix: "sup_" });

export type SupplierContactId = ID<"supplierContact">;
export const supplierContactIdFormat = idFormat<SupplierContactId>({
  prefix: "supc_",
});

export type SupplierProductId = ID<"supplierProduct">;
export const supplierProductIdFormat = idFormat<SupplierProductId>({
  prefix: "spr_",
});

export type PurchaseOrderId = ID<"purchaseOrder">;
export const purchaseOrderIdFormat = idFormat<PurchaseOrderId>({
  prefix: "po_",
});

export type PurchaseOrderRecommendationId = ID<"purchaseOrderRecommendation">;
export const purchaseOrderRecommendationIdFormat =
  idFormat<PurchaseOrderRecommendationId>({ prefix: "por_" });

export type PurchaseOrderRecommendationRuleId =
  ID<"purchaseOrderRecommendationRule">;
export const purchaseOrderRecommendationRuleIdFormat =
  idFormat<PurchaseOrderRecommendationRuleId>({ prefix: "porr_" });

export type PurchaseOrderProductId = ID<"purchaseOrderProduct">;
export const purchaseOrderProductIdFormat = idFormat<PurchaseOrderProductId>({
  prefix: "pop_",
});

export type InboundShipmentId = ID<"inboundShipment">;
export const inboundShipmentIdFormat = idFormat<InboundShipmentId>({
  prefix: "inb_",
});

export type PurchaseOrderFileId = ID<"purchaseOrderFile">;
export const purchaseOrderFileIdFormat = idFormat<PurchaseOrderFileId>({
  prefix: "pof_",
});

export type PoUploadJobId = ID<"poUploadJob">;
export const poUploadJobIdFormat = idFormat<PoUploadJobId>({ prefix: "puj_" });

export type PoUploadJobFileId = ID<"poUploadJobFile">;
export const poUploadJobFileIdFormat = idFormat<PoUploadJobFileId>({
  prefix: "puf_",
});

export type ImageId = ID<"image">;
export const imageIdFormat = idFormat<ImageId>({ prefix: "img_" });

export type AttributeDefinitionId = ID<"attributeDefinition">;
export const attributeDefinitionIdFormat = idFormat<AttributeDefinitionId>({
  prefix: "attr_",
});

export type CheckoutSessionId = ID<"checkoutSession">;
export const checkoutSessionIdFormat = idFormat<CheckoutSessionId>({
  prefix: "chs_",
});

export type CheckoutSessionItemId = ID<"checkoutSessionItem">;
export const checkoutSessionItemIdFormat = idFormat<CheckoutSessionItemId>({
  prefix: "chi_",
});

export type CheckoutFulfillmentOptionId = ID<"checkoutFulfillmentOption">;
export const checkoutFulfillmentOptionIdFormat =
  idFormat<CheckoutFulfillmentOptionId>({ prefix: "cfo_" });

export type CheckoutFulfillmentGroupId = ID<"checkoutFulfillmentGroup">;
export const checkoutFulfillmentGroupIdFormat =
  idFormat<CheckoutFulfillmentGroupId>({ prefix: "cfg_" });

export type CheckoutOrderId = ID<"checkoutOrder">;
export const checkoutOrderIdFormat = idFormat<CheckoutOrderId>({
  prefix: "cho_",
});

export type ProductCategoryId = ID<"productCategory">;
export const productCategoryIdFormat = idFormat<ProductCategoryId>({
  prefix: "pcat_",
});

export type HsCodeId = ID<"hsCode">;
export const hsCodeIdFormat = idFormat<HsCodeId>({ prefix: "hsc_" });

export type InboundShipmentProductId = ID<"inboundShipmentProduct">;
export const inboundShipmentProductIdFormat =
  idFormat<InboundShipmentProductId>({ prefix: "isp_" });

export type InboundShipmentFileId = ID<"inboundShipmentFile">;
export const inboundShipmentFileIdFormat = idFormat<InboundShipmentFileId>({
  prefix: "isf_",
});

export type ShipmentDisputeId = ID<"shipmentDispute">;
export const shipmentDisputeIdFormat = idFormat<ShipmentDisputeId>({
  prefix: "shd_",
});

export type ShipmentDisputePhotoId = ID<"shipmentDisputePhoto">;
export const shipmentDisputePhotoIdFormat = idFormat<ShipmentDisputePhotoId>({
  prefix: "sdp_",
});

export type ReturnsCenterId = ID<"returnsCenter">;
export const returnsCenterIdFormat = idFormat<ReturnsCenterId>({
  prefix: "rctr_",
});

export type ShopifyListingId = ID<"shopifyListing">;
export const shopifyListingIdFormat = idFormat<ShopifyListingId>({
  prefix: "sfl_",
});

export type ShopifyPublicationId = ID<"shopifyPublication">;
export const shopifyPublicationIdFormat = idFormat<ShopifyPublicationId>({
  prefix: "spub_",
});

export type ChatGptListingId = ID<"chatGptListing">;
export const chatGptListingIdFormat = idFormat<ChatGptListingId>({
  prefix: "cgpl_",
});

export type RedoMarketplaceListingId = ID<"redoMarketplaceListing">;
export const redoMarketplaceListingIdFormat =
  idFormat<RedoMarketplaceListingId>({ prefix: "rml_" });

export type EbayListingId = ID<"ebayListing">;
export const ebayListingIdFormat = idFormat<EbayListingId>({ prefix: "ebl_" });

export type EbayProductListingId = ID<"ebayProductListing">;
export const ebayProductListingIdFormat = idFormat<EbayProductListingId>({
  prefix: "epl_",
});

export type GoogleShoppingListingId = ID<"googleShoppingListing">;
export const googleShoppingListingIdFormat = idFormat<GoogleShoppingListingId>({
  prefix: "gsl_",
});

export type ProcessingSessionId = ID<"processingSession">;
export const processingSessionIdFormat = idFormat<ProcessingSessionId>({
  prefix: "rps_",
});

export type ProcessingGroupId = ID<"processingGroup">;
export const processingGroupIdFormat = idFormat<ProcessingGroupId>({
  prefix: "pg_",
});

export type SortingToteId = ID<"sortingTote">;
export const sortingToteIdFormat = idFormat<SortingToteId>({ prefix: "st_" });

export type StationProcessingSessionId = ID<"stationProcessingSession">;
export const stationProcessingSessionIdFormat =
  idFormat<StationProcessingSessionId>({ prefix: "sps_" });

export type SortDestinationId = ID<"sortDestination">;
export const sortDestinationIdFormat = idFormat<SortDestinationId>({
  prefix: "sd_",
});

export type StationProcessingSessionToteId = ID<"stationProcessingSessionTote">;
export const stationProcessingSessionToteIdFormat =
  idFormat<StationProcessingSessionToteId>({ prefix: "spst_" });

export type ProcessingSessionItemId = ID<"processingSessionItem">;
export const processingSessionItemIdFormat = idFormat<ProcessingSessionItemId>({
  prefix: "rpsi_",
});

export type BillOfMaterialsId = ID<"billOfMaterials">;
export const billOfMaterialsIdFormat = idFormat<BillOfMaterialsId>({
  prefix: "bom_",
});

export type BillOfMaterialsLocationId = ID<"billOfMaterialsLocation">;
export const billOfMaterialsLocationIdFormat =
  idFormat<BillOfMaterialsLocationId>({ prefix: "boml_" });

export type TransferOrderId = ID<"transferOrder">;
export const transferOrderIdFormat = idFormat<TransferOrderId>({
  prefix: "trn_",
});

export type TransferOrderProductId = ID<"transferOrderProduct">;
export const transferOrderProductIdFormat = idFormat<TransferOrderProductId>({
  prefix: "trp_",
});

export type TransferOrderFileId = ID<"transferOrderFile">;
export const transferOrderFileIdFormat = idFormat<TransferOrderFileId>({
  prefix: "trf_",
});

export type WorkOrderId = ID<"workOrder">;
export const workOrderIdFormat = idFormat<WorkOrderId>({ prefix: "wo_" });

export type WorkOrderStepId = ID<"workOrderStep">;
export const workOrderStepIdFormat = idFormat<WorkOrderStepId>({
  prefix: "wos_",
});

export type WorkOrderPickLineId = ID<"workOrderPickLine">;
export const workOrderPickLineIdFormat = idFormat<WorkOrderPickLineId>({
  prefix: "wpl_",
});

export type FreightBookingId = ID<"freightBooking">;
export const freightBookingIdFormat = idFormat<FreightBookingId>({
  prefix: "fb_",
});

export type FreightPickupWindowId = ID<"freightPickupWindow">;
export const freightPickupWindowIdFormat = idFormat<FreightPickupWindowId>({
  prefix: "fpw_",
});

export type FreightHandlingUnitId = ID<"freightHandlingUnit">;
export const freightHandlingUnitIdFormat = idFormat<FreightHandlingUnitId>({
  prefix: "fhu_",
});

export type FreightPickupAccessorialPresetId =
  ID<"freightPickupAccessorialPreset">;
export const freightPickupAccessorialPresetIdFormat =
  idFormat<FreightPickupAccessorialPresetId>({ prefix: "fpa_" });

export type FreightDeliveryAccessorialPresetId =
  ID<"freightDeliveryAccessorialPreset">;
export const freightDeliveryAccessorialPresetIdFormat =
  idFormat<FreightDeliveryAccessorialPresetId>({ prefix: "fda_" });

export type PriceBookId = ID<"priceBook">;
export const priceBookIdFormat = idFormat<PriceBookId>({ prefix: "pb_" });

export type ShipmentLegId = ID<"shipmentLeg">;
export const shipmentLegIdFormat = idFormat<ShipmentLegId>({ prefix: "shlg_" });

export type DownloadId = ID<"download">;
export const downloadIdFormat = idFormat<DownloadId>({ prefix: "dl_" });

export type ShipmentLegParcelId = ID<"shipmentLegParcel">;
export const shipmentLegParcelIdFormat = idFormat<ShipmentLegParcelId>({
  prefix: "shlgp_",
});

export type PriceBookEntryId = ID<"priceBookEntry">;
export const priceBookEntryIdFormat = idFormat<PriceBookEntryId>({
  prefix: "pbe_",
});

export type RedoSuppliesAccountPriceBookId = ID<"redoSuppliesAccountPriceBook">;
export const redoSuppliesAccountPriceBookIdFormat =
  idFormat<RedoSuppliesAccountPriceBookId>({ prefix: "rsapb_" });

export type AeoRecommendationId = ID<"aeoRecommendation">;
export const aeoRecommendationIdFormat = idFormat<AeoRecommendationId>({
  prefix: "aeorec_",
});

export type ExchangeAgentGuidanceId = ID<"exchangeAgentGuidance">;
export const exchangeAgentGuidanceIdFormat = idFormat<ExchangeAgentGuidanceId>({
  prefix: "eag_",
});

export type RedoCartId = ID<"redoCart">;
export const redoCartIdFormat = idFormat<RedoCartId>({ prefix: "rcart_" });

export type RedoCartItemId = ID<"redoCartItem">;
export const redoCartItemIdFormat = idFormat<RedoCartItemId>({
  prefix: "rci_",
});

export type RedoCartCheckoutId = ID<"redoCartCheckout">;
export const redoCartCheckoutIdFormat = idFormat<RedoCartCheckoutId>({
  prefix: "rcc_",
});

export type SuppliesProductMappingId = ID<"suppliesProductMapping">;
export const suppliesProductMappingIdFormat =
  idFormat<SuppliesProductMappingId>({ prefix: "spm_" });

export type SuppliesInventoryConfigId = ID<"suppliesInventoryConfig">;
export const suppliesInventoryConfigIdFormat =
  idFormat<SuppliesInventoryConfigId>({ prefix: "sic_" });

export type BrandCompetitorId = ID<"brandCompetitor">;
export const brandCompetitorIdFormat = idFormat<BrandCompetitorId>({
  prefix: "bcom_",
});

export type MarketId = ID<"market">;
export const marketIdFormat = idFormat<MarketId>({ prefix: "mkt_" });

export type MarketCountryId = ID<"marketCountry">;
export const marketCountryIdFormat = idFormat<MarketCountryId>({
  prefix: "mktc_",
});

export type VariantQuoteId = ID<"variantQuote">;
export const variantQuoteIdFormat = idFormat<VariantQuoteId>({ prefix: "vq_" });

export type HsRateStateId = ID<"hsRateState">;
export const hsRateStateIdFormat = idFormat<HsRateStateId>({ prefix: "hrs_" });

export type AllowedProblemPickPolicyId = ID<"allowedProblemPickPolicy">;
export const allowedProblemPickPolicyIdFormat =
  idFormat<AllowedProblemPickPolicyId>({ prefix: "appp_" });

export type ParcelPhotoId = ID<"parcelPhoto">;
export const parcelPhotoIdFormat = idFormat<ParcelPhotoId>({ prefix: "pph_" });

export type OrderId = ID<"order">;
export const orderIdFormat = idFormat<OrderId>({ prefix: "ordr_" });

export type ReturnId = ID<"return">;
export const returnIdFormat = idFormat<ReturnId>({ prefix: "rtrn_" });

export type ReturnShipmentGroupId = ID<"returnShipmentGroup">;
export const returnShipmentGroupIdFormat = idFormat<ReturnShipmentGroupId>({
  prefix: "rsg_",
});

export type ReturnShipmentId = ID<"returnShipment">;
export const returnShipmentIdFormat = idFormat<ReturnShipmentId>({
  prefix: "rsh_",
});

export type ReturnItemId = ID<"returnItem">;
export const returnItemIdFormat = idFormat<ReturnItemId>({ prefix: "rit_" });

export type CustomerId = ID<"customer">;
export const customerIdFormat = idFormat<CustomerId>({ prefix: "cust_" });

export type CustomerEventId = ID<"customerEvent">;
export const customerEventIdFormat = idFormat<CustomerEventId>({
  prefix: "cev_",
});

type ID<T> = string & { __idType: T };

export interface Crockford32Format<T extends string> {
  read(value: T): bigint;
  write(value: bigint): T;
  test(value: string): value is T;
  schema: z.ZodType<T, string>;
  readonly prefix: string;
}

function idFormat<T extends string>({
  prefix = "",
}: Crockford32Options = {}): Crockford32Format<T> {
  const { read, write } = crockford32StringFormat({ prefix });
  const result: Crockford32Format<T> = {
    get prefix() {
      return prefix;
    },
    read(value: T): bigint {
      return read(value);
    },
    write(value: bigint): T {
      return write(value) as T;
    },
    test(value: string): value is T {
      return !prefix || value.startsWith(prefix);
    },
    schema: z
      .custom<T>((value) => {
        return (
          typeof value === "bigint" ||
          typeof value === "number" ||
          (typeof value === "string" &&
            (value.startsWith(prefix) || /^\d+$/.test(value)))
        );
      })
      .transform((value) => {
        if (typeof value === "bigint") {
          return write(value) as T;
        }
        if (typeof value === "number") {
          return write(BigInt(value)) as T;
        }
        if (typeof value === "string" && /^\d+$/.test(value)) {
          return write(BigInt(value)) as T;
        }
        return value;
      })
      .meta({ annotation: "String" }) as z.ZodType<T, string>,
  };
  return result;
}

// redo-ids split: CRDB id formats (mongo helpers in @redotech/redo-ids-mongo).
